"use client";

import { useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X } from "lucide-react";
import AdminActions from "@/components/admin/card/admin-actions";
import ImageCarousel from "../carousel/image-carousel";
import FullscreenImageViewer from "../carousel/fullscreen-image-viewer";

type ItemLike = {
	id?: string | number;
	_id?: string;
	title?: string;
	image?: string;
	imageUrl?: string;
	previewImage?: string;
	description?: string;
	longDesc?: string;
	fullDescription?: string;
	carouselImages?: string[];
};

interface OverlayCardProps {
	selectedItem: ItemLike | null;
	onClose: () => void;
	isAdmin?: boolean;
	isNews?: boolean;
	manageContent?: boolean;
	contentEntity?: "news" | "section-gallery";
	onDelete?: (id: string | number) => void;
	fullscreen?: boolean;
	maxWidthClass?: string; // solo variante compatta
	maxHeightClass?: string; // solo variante compatta
}

export default function OverlayCard({
	selectedItem,
	onClose,
	isAdmin = false,
	isNews = false,
	manageContent = false,
	contentEntity,
	onDelete,
	fullscreen = false,
	maxWidthClass = "max-w-[840px]",
	maxHeightClass = "max-h-[80vh]",
}: OverlayCardProps) {
	const id = selectedItem?.id ?? selectedItem?._id ?? "";
	const showActions =
		!!id &&
		!String(id).startsWith("legacy-") &&
		((isAdmin && isNews) || (manageContent && contentEntity));
	const deleteEntity = contentEntity || (isNews ? "news" : undefined);
	const fullDesc =
		selectedItem?.fullDescription ||
		selectedItem?.longDesc ||
		selectedItem?.description ||
		"";
	const imageToShow =
		selectedItem?.image ||
		selectedItem?.imageUrl ||
		selectedItem?.previewImage ||
		selectedItem?.carouselImages?.[0];

	// blocca lo scroll del body quando l’overlay è aperto
	useEffect(() => {
		if (!selectedItem) return;
		const original = document.body.style.overflow;
		document.body.style.overflow = "hidden";
		return () => {
			document.body.style.overflow = original;
		};
	}, [selectedItem]);

	// ESC per chiudere
	useEffect(() => {
		if (!selectedItem) return;
		const onKey = (e: KeyboardEvent) => {
			if (e.key === "Escape") onClose();
		};
		window.addEventListener("keydown", onKey);
		return () => window.removeEventListener("keydown", onKey);
	}, [selectedItem, onClose]);

	const dialogBase =
		"bg-(--card) text-left shadow-xl relative flex flex-col overflow-hidden";

	const dialogSize = fullscreen
		? "w-screen h-[100dvh] rounded-none"
		: `w-[92vw] ${maxWidthClass} ${maxHeightClass} rounded-2xl`;

	return (
		<AnimatePresence>
			{selectedItem && (
				<motion.div
					initial={{ opacity: 0 }}
					animate={{ opacity: 1 }}
					exit={{ opacity: 0 }}
					className="fixed inset-0 z-50 bg-(--background)/60 backdrop-blur-sm flex items-center justify-center overscroll-contain"
					onClick={(e) => {
						if (e.currentTarget === e.target) onClose();
					}}
				>
					<motion.div
						role="dialog"
						aria-modal="true"
						initial={{ scale: 0.98, opacity: 0 }}
						animate={{ scale: 1, opacity: 1 }}
						exit={{ scale: 0.98, opacity: 0 }}
						transition={{ duration: 0.25, ease: "easeOut" }}
						className={`${dialogBase} ${dialogSize}`}
					>
						{/* Close */}
						<button
							className="absolute top-4 right-4 z-10 text-(--foreground)"
							onClick={onClose}
							aria-label="Chiudi"
						>
							<X className="w-6 h-6 cursor-pointer" />
						</button>

						{fullscreen ? (
							// ✅ FULLSCREEN: carousel sempre visibile + testo scroll separato
							<div className="h-full min-h-0 p-6 sm:p-8 flex flex-col justify-center">
								{/* Body */}
								<div className="mt-4 flex-1 min-h-0 grid grid-rows-[auto,1fr] md:grid-rows-1 md:grid-cols-2 gap-6">
									{/* Carousel */}
									<div className="min-h-[320px] md:min-h-0 md:h-full overflow-hidden rounded-xl border border-black/5 bg-white/60">
										{imageToShow ? (
											<FullscreenImageViewer
												image={imageToShow}
												className="h-full"
											/>
										) : (
											<div className="h-full w-full flex items-center justify-center text-(--muted-foreground)">
												Nessuna immagine disponibile
											</div>
										)}
									</div>

									{/* Testo (scroll) */}
									<div className="min-h-0 overflow-y-auto pr-2">
										{/* Azioni admin */}
										{showActions && deleteEntity && (
											<div className="mt-6">
												<AdminActions
													id={id as string}
													entity={deleteEntity}
													onDeleted={() => {
														onClose();
														onDelete?.(id as string);
													}}
												/>
											</div>
										)}
									</div>
								</div>
							</div>
						) : (
							// ✅ COMPACT: come prima (scroll unico)
							<div className="p-6 sm:p-8 flex-1 min-h-0 flex flex-col overflow-hidden">
								<div className="flex-1 min-h-0 overflow-y-auto pr-2">
									<h3 className="text-2xl font-bold mb-4 text-(--card-foreground)">
										{selectedItem.title}
									</h3>

									{fullDesc && (
										<div className="text-(--foreground) leading-relaxed mb-6 whitespace-pre-line">
											{fullDesc}
										</div>
									)}

									{!!selectedItem.carouselImages?.length && (
										<ImageCarousel
											key={String(id)}
											images={selectedItem.carouselImages}
											className="mb-4"
										/>
									)}

									{showActions && deleteEntity && (
										<div className="mt-6">
											<AdminActions
												id={id as string}
												entity={deleteEntity}
												onDeleted={() => {
													onClose();
													onDelete?.(id as string);
												}}
											/>
										</div>
									)}
								</div>
							</div>
						)}
					</motion.div>
				</motion.div>
			)}
		</AnimatePresence>
	);
}
